/**
 * @author Jorge Casas
 * @creationDate 10/10/2025
 * @path src/components/dashboard/AssetStatusSummary.js
 */
import React from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import '../../dashboard.css';

function fmt(n) {
  try {
    return new Intl.NumberFormat('es-MX').format(Number(n) || 0);
  } catch (_) {
    return String(n || 0);
  }
}
function pct(part, total) {
  const t = Number(total) || 0,
    p = Number(part) || 0;
  return t > 0 ? Math.round((p / t) * 1000) / 10 + '%' : '0%';
}
function tr(t, key, fallback) {
  const out = t ? t(key) : key;
  return !out || out === key ? fallback : out;
}

function AssetStatusSummary(props) {
  const t = props.t || ((s) => s);
  const s = props.summary || {};

  const assigned = Number(s.assigned) || 0;
  const unassigned = Number(s.unassigned) || 0;
  const decommissioned = Number(s.decommissioned) || 0;
  const depreciating = Number(s.depreciating != null ? s.depreciating : s.maintenance) || 0;

  const total =
    s.total != null ? Number(s.total) : assigned + unassigned + decommissioned + depreciating;

  const cards = [
    {
      key: 'assigned',
      className: 'assigned',
      icon: 'fa fa-check-circle',
      label: tr(t, 'common.assigned', 'Asignados'),
      value: assigned,
    },
    {
      key: 'unassigned',
      className: 'unassigned',
      icon: 'fa fa-user-o',
      label: tr(t, 'common.unassigned', 'Sin asignar'),
      value: unassigned,
    },
    {
      key: 'decommissioned',
      className: 'decommissioned',
      icon: 'fa fa-times-circle',
      label: tr(t, 'common.decommissioned', 'Dados de baja'),
      value: decommissioned,
    },
    {
      key: 'depreciating',
      className: 'depreciating',
      icon: 'fa fa-line-chart',
      label: tr(t, 'common.depreciating', 'Depreciándose'),
      value: depreciating,
    },
  ];
  if (props.showTotal !== false) {
    cards.unshift({
      key: 'total',
      className: 'total',
      icon: 'fa fa-archive',
      label: tr(t, 'common.total', 'Total'),
      value: total,
    });
  }

  const clickable = Boolean(props.onSelect || (props.links && Object.keys(props.links).length));
  const handleClick = (k) => {
    if (props.onSelect && typeof props.onSelect === 'function') props.onSelect(k);
    const href = props.links && props.links[k];
    if (href && typeof window !== 'undefined') window.location.href = href;
  };

  return (
    <div className="col-lg-12">
      <div className="kpi-section--inline">
        <div className="kpi-section__header">
          {t('dashboard.assetsSummary', 'RESUMEN DE ACTIVOS')}
        </div>

        <div className="kpi-section__body">
          <div className="kpi-cards-container kpi-grid kpi-grid--compact kpi-grid--one-row">
            {cards.map((c) => {
              const percent = c.key === 'total' ? null : pct(c.value, total);
              return (
                <div
                  key={c.key}
                  className={
                    'kpi-card kpi-card--compact ' + c.className + (clickable ? ' clickable' : '')
                  }
                  onClick={clickable ? () => handleClick(c.key) : undefined}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => (e.key === 'Enter' || e.key === ' ') && handleClick(c.key)
                      : undefined
                  }
                  title={c.label}
                >
                  <div className="kpi-head">
                    <i className={'kpi-icon ' + c.icon} aria-hidden="true" />
                    <span className="kpi-title">{c.label}</span>
                  </div>
                  <div className="kpi-metric">{fmt(c.value)}</div>
                  {percent != null && <div className="kpi-sub">{percent} del total</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

AssetStatusSummary.propTypes = {
  summary: PropTypes.shape({
    total: PropTypes.number,
    assigned: PropTypes.number,
    unassigned: PropTypes.number,
    decommissioned: PropTypes.number,
    depreciating: PropTypes.number,
  }),
  showTotal: PropTypes.bool,
  onSelect: PropTypes.func,
  links: PropTypes.object,
  t: PropTypes.func,
};

AssetStatusSummary.defaultProps = {
  summary: { total: 0, assigned: 0, unassigned: 0, decommissioned: 0, depreciating: 0 },
  showTotal: true,
};

export default translate()(AssetStatusSummary);



// WEBPACK FOOTER //
// src/components/dashboard/AssetStatusSummary.js